"use client";

import { useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Play, X } from "lucide-react";
import { useTour } from "./tour-provider";
import {
  shouldShowReminderBanner,
  incrementBannerDismiss,
} from "./tour-nagging";
import { useNavigationLoading } from "@/components/shell/navigation-loading-context";

interface TourReminderBannerProps {
  userId?: string;
  tourCompleted?: boolean;
}

export function TourReminderBanner({
  userId,
  tourCompleted = false,
}: TourReminderBannerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { isRunning, isWelcomeOpen, startTour } = useTour();
  const { startNavigation } = useNavigationLoading();
  const [dismissed, setDismissed] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  const sessionStartRef = useRef(Date.now());

  useEffect(() => {
    if (tourCompleted || isRunning || isWelcomeOpen || dismissed) {
      setShouldShow(false);
      return;
    }

    setShouldShow(shouldShowReminderBanner(userId, sessionStartRef.current));
  }, [tourCompleted, isRunning, isWelcomeOpen, dismissed, userId]);

  const handleDismiss = () => {
    setDismissed(true);
    incrementBannerDismiss(userId);
  };

  const handleStartTour = () => {
    if (pathname !== "/optimizer") {
      startNavigation("/optimizer?restart_tour=true");
      router.push("/optimizer?restart_tour=true");
    } else {
      startTour();
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="border-b border-surface-line bg-surface-raised px-3 sm:px-4">
      <div className="mx-auto flex h-10 max-w-7xl items-center gap-3.5">
        <span className="data-num inline-flex h-[22px] flex-shrink-0 items-center gap-1.5 rounded border border-primary/35 bg-primary/[0.12] px-2 font-mono text-[11px] font-semibold tracking-[0.08em] text-primary">
          TOUR <span className="opacity-70">90s</span>
        </span>
        <p className="hidden min-w-0 flex-1 truncate text-[13px] text-zinc-500 sm:block">
          <span className="font-semibold text-foreground">Three clicks to your first +EV bet.</span>
          <span> Where the edge is, how to read it, where to bet it.</span>
        </p>
        <span className="flex-1 sm:hidden" />
        <button
          type="button"
          onClick={handleStartTour}
          className="tap inline-flex h-7 flex-shrink-0 items-center gap-2 rounded-md bg-primary pl-3 pr-2.5 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Play the tour
          <Play className="size-3 fill-current" />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="tap min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0 rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
